<script lang="ts">
	import { onDestroy, onMount, tick } from 'svelte';
	import { Icon } from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import type { VerivoxDismissSurface } from 'src/components/PromotionalIntegrations/Verivox/utils/verivoxDismissals';
	import { resolveVisibleDashPromoBanners } from 'src/configs/dashboards/energy-dashboard-promo-banners';
	import { profile_data } from 'src/stores/account/user';
	import {
		dashPromoActiveSampleIds,
		dashPromoDismissals,
		dashPromoMaxBanners,
	} from 'src/stores/features/dev/dashboard-promo-samples';
	import {
		dashPromoEmptyStateEnabled,
		globalInlineBannersEnabled,
	} from 'src/stores/features/dev/dashboard-promo-slots';
	import { verivoxDevForceEnable } from 'src/stores/features/dev/verivox-dev-force-enable';
	import { isVerivoxEligibleForDashPromos } from 'src/helpers/verivox_promo_helpers';
	import DashPromoGalleryWidget from 'src/components/Dashboards/DashWidgets/Components/DashPromoGalleryWidget.svelte';
	import DashPromoEmptyState from 'src/components/Dashboards/DashWidgets/Components/DashPromoEmptyState.svelte';
	import type { DashPromoGallerySlide } from 'src/components/Dashboards/dash-promo-types';
	import CollapseGap from 'src/components/Utility/CollapseGap.svelte';
	import { PROMO_COLLAPSE_MS, prefersReducedMotion } from 'src/helpers/ui_transitions';
	import { mapPromoBannersToSlides } from './map-promo-banners-to-slides';

	/**
	 * GlobalInlineBanners — list-page promotions strip.
	 * - ≤600px: carousel viewport (shared engine with BannersWidget)
	 * - >600px: horizontal scroller with Tabs-like edge dissolve + per-banner dismiss
	 * Banner pool / dismissals are shared with the dashboard BannersWidget via Promotions config.
	 */
	export let surface: VerivoxDismissSurface;
	/** When false, renders nothing. Combined with globalInlineBannersEnabled / Verivox eligibility. */
	export let enabled = true;

	const BANNER_HEIGHT_PX = 120;
	const BANNER_WIDTH_PX = 280;
	/** Rail width collapse — shared with container height collapse. */
	const DISMISS_MS = PROMO_COLLAPSE_MS;
	/** At/below: gallery viewport. Above: horizontal scroll rail. */
	const LAYOUT_BREAKPOINT_PX = 600;

	let windowInnerWidth = 0;
	let scrollerEl: HTMLDivElement | null = null;
	let collapseGap: { lockHeight: () => void } | undefined;
	let showLeftFade = false;
	let showRightFade = false;
	let displaySlides: DashPromoGallerySlide[] = [];
	let exitingIds: Record<string, boolean> = {};
	let itemWidths: Record<string, number> = {};
	let itemEls: Record<string, HTMLDivElement> = {};

	$: isNarrow = windowInnerWidth > 0 && windowInnerWidth <= LAYOUT_BREAKPOINT_PX;

	$: sourceSlides = (() => {
		void $profile_data;
		void $verivoxDevForceEnable;
		void $dashPromoActiveSampleIds;
		void $dashPromoDismissals;
		void $dashPromoMaxBanners;
		void $globalInlineBannersEnabled;

		if (!enabled) return [] as DashPromoGallerySlide[];
		const slotOn = $globalInlineBannersEnabled;
		if (!slotOn && !isVerivoxEligibleForDashPromos()) {
			return [] as DashPromoGallerySlide[];
		}
		return mapPromoBannersToSlides(
			resolveVisibleDashPromoBanners(surface, 'inline', slotOn),
			{
				maxBanners: $dashPromoMaxBanners,
				compact: true,
			},
		);
	})();

	/** Keep local list stable during dismiss collapse animations. */
	$: syncDisplaySlides(sourceSlides);

	const hasExiting = () => Object.values(exitingIds).some(Boolean);

	$: showEmptyState =
		enabled &&
		$globalInlineBannersEnabled &&
		displaySlides.length === 0 &&
		$dashPromoEmptyStateEnabled &&
		!hasExiting();

	$: shouldShowStrip =
		enabled && (displaySlides.length > 0 || showEmptyState);

	const railDismissDurationMs = () => (prefersReducedMotion() ? 0 : DISMISS_MS);

	const syncDisplaySlides = (next: DashPromoGallerySlide[]) => {
		const exiting = new Set(Object.keys(exitingIds).filter((id) => exitingIds[id]));
		const nextIds = new Set(next.map((s) => s.id));

		const merged = displaySlides.filter((s) => nextIds.has(s.id) || exiting.has(s.id));
		const known = new Set(merged.map((s) => s.id));
		let result = merged;
		for (const slide of next) {
			if (!known.has(slide.id)) {
				result = [...result, slide];
			} else {
				result = result.map((s) => (s.id === slide.id ? slide : s));
			}
		}

		// Gallery dismiss clears source before rail exitingIds — lock for list reflow.
		if (
			displaySlides.length > 0 &&
			result.length === 0 &&
			!$dashPromoEmptyStateEnabled
		) {
			collapseGap?.lockHeight();
		}

		displaySlides = result;
	};

	const bindItem = (node: HTMLDivElement, id: string) => {
		itemEls[id] = node;
		return {
			update(newId: string) {
				delete itemEls[id];
				id = newId;
				itemEls[id] = node;
			},
			destroy() {
				delete itemEls[id];
			},
		};
	};

	const updateEdgeFades = () => {
		if (!scrollerEl) {
			showLeftFade = false;
			showRightFade = false;
			return;
		}
		const { scrollLeft, scrollWidth, clientWidth } = scrollerEl;
		showLeftFade = scrollLeft > 2;
		showRightFade = scrollLeft + clientWidth < scrollWidth - 2;
	};

	const dismissSlide = async (slide: DashPromoGallerySlide) => {
		if (!slide.onDismiss || exitingIds[slide.id]) return;

		const el = itemEls[slide.id];
		const width = el?.getBoundingClientRect().width ?? BANNER_WIDTH_PX;
		itemWidths[slide.id] = width;
		itemWidths = itemWidths;
		await tick();

		exitingIds[slide.id] = true;
		exitingIds = exitingIds;

		try {
			await slide.onDismiss();
		} catch {
			// still collapse so UI matches dismissed state
		}

		await new Promise((r) => setTimeout(r, railDismissDurationMs()));

		// Lock before removing the last banner so the list gap can ease shut.
		if (displaySlides.length <= 1) {
			collapseGap?.lockHeight();
		}

		displaySlides = displaySlides.filter((s) => s.id !== slide.id);
		const { [slide.id]: _e, ...restExit } = exitingIds;
		exitingIds = restExit;
		const { [slide.id]: _w, ...restWidth } = itemWidths;
		itemWidths = restWidth;

		await tick();
		updateEdgeFades();
	};

	onMount(() => {
		updateEdgeFades();
	});

	$: if (!isNarrow && displaySlides.length >= 0) {
		void tick().then(updateEdgeFades);
	}

	onDestroy(() => {
		itemEls = {};
	});
</script>

<svelte:window bind:innerWidth={windowInnerWidth} />

<CollapseGap
	bind:this={collapseGap}
	when={shouldShowStrip}
	hold={hasExiting()}
>
	<section
		class="global-inline-banners"
		class:global-inline-banners--narrow={isNarrow}
		aria-label={$format('heading_promotions', { default: 'Promotions' })}
	>
		{#if isNarrow}
			{#if displaySlides.length > 0}
				<DashPromoGalleryWidget slides={displaySlides} heightPx={BANNER_HEIGHT_PX} />
			{:else if showEmptyState}
				<div class="global-inline-banners__empty">
					<DashPromoEmptyState compact />
				</div>
			{/if}
		{:else if showEmptyState && displaySlides.length === 0}
			<div class="global-inline-banners__empty global-inline-banners__empty--rail">
				<DashPromoEmptyState compact />
			</div>
		{:else}
			<div
				class="global-inline-banners__viewport"
				class:global-inline-banners__viewport--fade-left={showLeftFade}
				class:global-inline-banners__viewport--fade-right={showRightFade}
			>
				<div
					class="global-inline-banners__scroller"
					bind:this={scrollerEl}
					on:scroll={updateEdgeFades}
				>
					{#each displaySlides as slide, i (slide.id)}
						{@const isExiting = Boolean(exitingIds[slide.id])}
						{@const lockedWidth = itemWidths[slide.id] ?? BANNER_WIDTH_PX}
						{@const isLast = i === displaySlides.length - 1}
						<div
							class="global-inline-banners__item"
							class:global-inline-banners__item--exiting={isExiting}
							use:bindItem={slide.id}
							style="width: {isExiting ? 0 : lockedWidth}px; min-width: {isExiting
								? 0
								: lockedWidth}px; margin-right: {isExiting || isLast
								? 0
								: 12}px; opacity: {isExiting ? 0 : 1};"
						>
							<div
								class="global-inline-banners__card"
								style="height: {BANNER_HEIGHT_PX}px; width: {BANNER_WIDTH_PX}px;"
							>
								<svelte:component this={slide.component} {...slide.props} />

								{#if slide.dismissable !== false && slide.onDismiss}
									<button
										type="button"
										class="global-inline-banners__dismiss reset-button-styles-base"
										aria-label={$format('button_dismiss', {
											default: 'Dismiss',
										})}
										on:click={() => dismissSlide(slide)}
									>
										<Icon icon="fal fa-times" size="s" />
									</button>
								{/if}
							</div>
						</div>
					{/each}
				</div>
			</div>
		{/if}
	</section>
</CollapseGap>

<style>
	.global-inline-banners {
		width: 100%;
	}

	.global-inline-banners__empty {
		width: 100%;
		height: 120px;
		max-height: 120px;
		border-radius: 12px;
		overflow: hidden;
		border: 1px solid rgba(255, 255, 255, 0.12);
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
		box-sizing: border-box;
	}

	.global-inline-banners__empty--rail {
		max-width: 280px;
	}

	.global-inline-banners__viewport {
		--promo-strip-fade-left: 0px;
		--promo-strip-fade-right: 0px;
		width: 100%;
		overflow: hidden;
	}

	.global-inline-banners__viewport--fade-left {
		--promo-strip-fade-left: 32px;
	}

	.global-inline-banners__viewport--fade-right {
		--promo-strip-fade-right: 32px;
	}

	.global-inline-banners__scroller {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		align-items: stretch;
		gap: 0;
		overflow-x: auto;
		overflow-y: hidden;
		scroll-behavior: smooth;
		-webkit-overflow-scrolling: touch;
		scrollbar-width: none;
		-ms-overflow-style: none;
		-webkit-mask-image: linear-gradient(
			to right,
			transparent 0,
			#000 var(--promo-strip-fade-left),
			#000 calc(100% - var(--promo-strip-fade-right)),
			transparent 100%
		);
		mask-image: linear-gradient(
			to right,
			transparent 0,
			#000 var(--promo-strip-fade-left),
			#000 calc(100% - var(--promo-strip-fade-right)),
			transparent 100%
		);
	}

	.global-inline-banners__scroller::-webkit-scrollbar {
		display: none;
	}

	.global-inline-banners__item {
		flex: 0 0 auto;
		overflow: hidden;
		transition:
			width 220ms cubic-bezier(0.22, 1, 0.36, 1),
			min-width 220ms cubic-bezier(0.22, 1, 0.36, 1),
			margin-right 220ms cubic-bezier(0.22, 1, 0.36, 1),
			opacity 180ms ease-out;
	}

	@media (prefers-reduced-motion: reduce) {
		.global-inline-banners__item {
			transition: none;
		}
	}

	.global-inline-banners__item--exiting {
		pointer-events: none;
	}

	.global-inline-banners__card {
		position: relative;
		width: 280px;
		max-width: 100%;
		border-radius: 12px;
		overflow: hidden;
		border: 1px solid rgba(255, 255, 255, 0.12);
		box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
		box-sizing: border-box;
	}

	.global-inline-banners__card > :global(*) {
		height: 100%;
		min-height: 0;
	}

	.global-inline-banners__dismiss {
		position: absolute;
		top: 6px;
		right: 6px;
		z-index: 7;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 22px;
		height: 22px;
		border-radius: 5px;
		color: #ffffff;
		cursor: pointer;
		opacity: 0.92;
		background: rgba(0, 0, 0, 0.22);
		backdrop-filter: blur(4px);
	}

	.global-inline-banners__dismiss:hover {
		opacity: 1;
		background: rgba(0, 0, 0, 0.35);
	}
</style>
